import { useEffect, useState, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import AttendancePage from "./pages/AttendancePage";
import WeeklySheetPage from "./pages/WeeklySheetPage";
import BrpPage from "./pages/BrpPage";
import GoalsPage from "./pages/GoalsPage";
import AssessmentPage from "./pages/AssessmentPage";
import StudentsPage from "./pages/StudentsPage";
import StudentDetailPage from "./pages/StudentDetailPage";
import ReferralTrackerPage from "./pages/ReferralTrackerPage";
import ReportsPage from "./pages/ReportsPage";
import CalendarPage from "./pages/CalendarPage";
import PermissionsPage from "./pages/PermissionsPage";
import AdvisoryPage from "./pages/AdvisoryPage";
import ProficiencyGridPage from "./pages/ProficiencyGridPage";
import ProgressTrackingPage from "./pages/ProgressTrackingPage";
import SettingsPage from "./pages/SettingsPage";
import StaffFilesPage from "./pages/StaffFilesPage";
import BillingDashboardPage from "./pages/BillingDashboardPage";
import ContactsPage from "./pages/ContactsPage";
import CommunicationsPage from "./pages/CommunicationsPage";
import QuickTasksPage from "./pages/QuickTasksPage";
import SelfAssessmentPage from "./pages/SelfAssessmentPage";
import ArchivesPage from "./pages/ArchivesPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import DataDeletionPage from "./pages/DataDeletionPage";
import MarketingPage from "./pages/MarketingPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import PortalPage from "./pages/PortalPage";
import ForceChangePassword from "./components/ForceChangePassword";
import PrivacyGate from "./components/PrivacyGate";

// Components
import NotificationBell from "./components/NotificationBell";
import GlobalSearch from "./components/GlobalSearch";

// Contexts
import { SettingsProvider, useSettings } from "./contexts/SettingsContext";

// API
import { getMe, getPortalMe, seedData } from "./lib/api";
import { getRiskConfig } from "./lib/api";
import { setRiskBands } from "./components/risk/riskConstants";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("token");
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      if (token) {
        try {
          if (stored?.is_portal) {
            const res = await getPortalMe();
            setUser({ ...res.data, is_portal: true });
          } else {
            const res = await getMe();
            setUser(res.data);
          }
        } catch (err) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = (userData, token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    // Fetch the full profile (incl. effective permissions) right after login.
    const fetchProfile = userData?.is_portal ? getPortalMe : getMe;
    fetchProfile().then((res) => {
      const merged = userData?.is_portal ? { ...res.data, is_portal: true } : res.data;
      setUser(merged);
      localStorage.setItem("user", JSON.stringify(merged));
    }).catch(() => {});
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const isPortal = user?.is_portal;
      const res = await (isPortal ? getPortalMe() : getMe());
      const merged = isPortal ? { ...res.data, is_portal: true } : res.data;
      setUser(merged);
      localStorage.setItem("user", JSON.stringify(merged));
      return merged;
    } catch {
      return null;
    }
  };

  const hasRole = (role) => {
    return user?.positions?.includes(role) || false;
  };

  const isAdmin = () => hasRole("Admin");
  const isTester = () => hasRole("Tester");
  const isHQViewer = () => hasRole("HQ Viewer") && !hasRole("Admin");

  // HQ Viewer sees every section (read-only), so it holds every permission.
  const hasPermission = (perm) => isAdmin() || hasRole("HQ Viewer") || (user?.permissions || []).includes(perm);

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser, loading, hasRole, isAdmin, isTester, isHQViewer, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Platform super admin never uses the school app — funnel them to the dashboard.
  if (user.is_super_admin) {
    return <Navigate to="/platform" replace />;
  }

  // External district-portal reps use their own restricted portal.
  if (user.is_portal) {
    return <Navigate to="/portal" replace />;
  }

  if (user.must_change_password) {
    return <ForceChangePassword />;
  }

  return children;
};

// External District Portal Route — for LEA / Medicaid Billing reps only.
const PortalRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!user.is_portal) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Super Admin (platform) Route — hidden dashboard, only reachable by the super admin.
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!user.is_super_admin) {
    return <Navigate to="/" replace />;
  }
  if (user.must_change_password) {
    return <ForceChangePassword />;
  }
  return children;
};

// Admin-only Route — redirects non-admins to home (blocks direct URL access).
// The read-only HQ Viewer is allowed through (it can VIEW every section); all
// writes are blocked server-side and by the API interceptor.
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdmin() && !hasRole("HQ Viewer")) {
    return <Navigate to="/" replace />;
  }
  return children;
};


// Maps a route path to the permission that gates it (mirrors the sidebar navItems).
// Used to enforce the "locked" teaser on direct URL access.
const NAV_PERM_MAP = [
  ["/attendance", "attendance"],
  ["/weekly-sheet", "attendance"],
  ["/assessment", "assessments"],
  ["/communications", "communications"],
  ["/quick-tasks", "communications"],
  ["/advisory", "advisory"],
  ["/students", "students"],
  ["/brp", "students"],
  ["/goals", "students"],
  ["/calendar", "calendar"],
  ["/reports", "reports"],
  ["/contacts", "contacts"],
  ["/progress-tracking", "progress_tracking"],
  ["/proficiency-grid", "progress_tracking"],
  ["/referrals", "referrals"],
  ["/settings", "settings"],
  ["/billing", "billing_dashboard"],
  ["/permissions", "__admin__"],
  ["/archives", "__admin__"],
  ["/staff-files", "__admin__"],
];

function permForPath(pathname) {
  if (pathname === "/") return "dashboard";
  const hit = NAV_PERM_MAP.find(([p]) => pathname === p || pathname.startsWith(p + "/"));
  return hit ? hit[1] : null;
}

// Reports-page tabs that are surfaced as sidebar shortcuts. Used so the "Reports and
// Lists" page item doesn't highlight at the same time as one of its tab shortcuts.
const SHORTCUT_TABS = ["create", "medical", "group-lists"];

// Standalone Dashboard item (never inside a group).
const DASHBOARD_ITEM = { path: "/", label: "Dashboard", icon: "grid-2x2", perm: "dashboard" };

// Collapsible sidebar groups. This is a pure navigation restructure — every `path`,
// `perm`, and the set of destinations is unchanged from the previous flat nav. Shortcut
// items (shortcut:true) open an existing tab on the Reports and Lists page; they follow
// the same "reports" permission as that page and create no new routes.
const NAV_GROUPS = [
  { key: "daily-operations", name: "Daily Operations", icon: "calendar-check", items: [
    { path: "/attendance", label: "Attendance", icon: "clipboard-check", perm: "attendance" },
    { path: "/assessment", label: "Assessments", icon: "file-text", perm: "assessments" },
    { path: "/calendar", label: "Schedules & Calendar", icon: "calendar", perm: "calendar" },
    { path: "/advisory", label: "Advisory", icon: "users-round", perm: "advisory" },
  ] },
  { key: "student-tracking", name: "Student Tracking", icon: "users", items: [
    { path: "/students", label: "Student Files", icon: "users", perm: "students" },
    { path: "/progress-tracking", label: "Progress Tracking", icon: "trending-up", perm: "progress_tracking" },
    { path: "/goals", label: "Goals", icon: "target", perm: "students" },
  ] },
  { key: "behavior-support", name: "Behavior & Support", icon: "life-buoy", items: [
    { path: "/brp", label: "BRP", icon: "brain", perm: "students" },
    { path: "/reports", tab: "create", query: "?tab=create", label: "Submit a Report", icon: "file-plus", perm: "reports", shortcut: true },
  ] },
  { key: "contacts-messaging", name: "Contacts & Messaging", icon: "message-circle", items: [
    { path: "/contacts", label: "Contacts", icon: "contact", perm: "contacts" },
    { path: "/communications", label: "Communications", icon: "message-square", perm: "communications" },
    { path: "/quick-tasks", label: "Quick Tasks", icon: "check-square", perm: "communications" },
  ] },
  { key: "reports-records", name: "Reports & Records", icon: "bar-chart-2", items: [
    { path: "/reports", label: "Reports and Lists", icon: "bar-chart-2", perm: "reports" },
    { path: "/reports", tab: "create", query: "?tab=create", label: "Submit a Report", icon: "file-plus", perm: "reports", shortcut: true },
    { path: "/reports", tab: "medical", query: "?tab=medical", label: "OTC Medical", icon: "pill", perm: "reports", shortcut: true },
    { path: "/reports", tab: "group-lists", query: "?tab=group-lists", label: "Print a List", icon: "list", perm: "reports", shortcut: true },
    { path: "/archives", label: "Archives", icon: "archive", perm: "__admin__" },
  ] },
  { key: "administration", name: "Administration", icon: "briefcase", items: [
    { path: "/billing", label: "Billing", icon: "dollar-sign", perm: "billing_dashboard" },
    { path: "/staff-files", label: "Staff Files", icon: "folder-lock", perm: "__admin__" },
    { path: "/referrals", label: "Referral Tracker", icon: "user-plus", perm: "referrals" },
    { path: "/weekly-sheet", label: "Weekly Sheet", icon: "truck", perm: "attendance" },
    { path: "/permissions", label: "Permissions", icon: "shield", perm: "__admin__" },
    { path: "/settings", label: "Settings", icon: "settings", perm: "settings" },
  ] },
];

// Main Layout with Sidebar
const MainLayout = ({ children }) => {
  const { user, logout, isAdmin, isTester, hasPermission, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();
  const [sidebarOpen, setSidebarOpen] = useState(!settings.mobileMode);

  // Auto-detect mobile based on screen width
  useEffect(() => {
    const checkMobile = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update sidebar state when mobile mode changes
  useEffect(() => {
    setSidebarOpen(!settings.mobileMode);
  }, [settings.mobileMode]);

  const isMobile = settings.mobileMode || (typeof window !== 'undefined' && window.innerWidth < 768);

  // Enforce the nav "teaser" lock on direct URL access: redirect locked pages to the
  // Dashboard so no data loads for a section the user can't access.
  const currentPerm = permForPath(location.pathname);
  const currentLocked = !!currentPerm && !(isAdmin() || hasRole("HQ Viewer") || (currentPerm !== "__admin__" && hasPermission(currentPerm)));
  useEffect(() => {
    if (currentLocked) {
      toast.error("That section is locked. Ask your administrator for access.");
    }
  }, [currentLocked, location.pathname]);

  // Load configurable risk-assessment score bands once authenticated.
  useEffect(() => {
    getRiskConfig().then((r) => setRiskBands(r.data.bands)).catch(() => {});
  }, []);

  // Close sidebar on navigation in mobile mode
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  // Collapsible-group accordion state (only one group open at a time). Persisted between
  // visits; Daily Operations is open by default for first-time users.
  const currentTab = new URLSearchParams(location.search).get("tab");
  const [openGroup, setOpenGroup] = useState(() => {
    try { return localStorage.getItem("sidebarOpenGroup") || "daily-operations"; } catch { return "daily-operations"; }
  });
  useEffect(() => {
    try { if (openGroup) localStorage.setItem("sidebarOpenGroup", openGroup); } catch { /* storage unavailable */ }
  }, [openGroup]);

  // Is the currently-viewed page this nav item? Shortcuts match the Reports tab; the
  // Reports and Lists page item is active only when NOT on one of the shortcut tabs.
  const isItemActive = (item) => {
    if (item.shortcut) return location.pathname === "/reports" && currentTab === item.tab;
    if (item.path === "/reports") return location.pathname === "/reports" && !SHORTCUT_TABS.includes(currentTab);
    return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  };
  const groupHasActive = (g) => g.items.some(isItemActive);

  // Auto-expand the group that contains the current page. If the already-open group
  // contains it (e.g. the user clicked a shortcut that lives in two groups), keep that
  // one open — so "the group the user clicked from" wins.
  useEffect(() => {
    const openG = NAV_GROUPS.find((g) => g.key === openGroup);
    if (openG && groupHasActive(openG)) return;
    const match = NAV_GROUPS.find(groupHasActive);
    if (match) setOpenGroup(match.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // A nav item is unlocked if the user is an admin, the read-only HQ Viewer (sees
  // everything), or (for non-admin-only items) holds the required permission.
  const isUnlocked = (item) =>
    isAdmin() || hasRole("HQ Viewer") || (item.perm !== "__admin__" && hasPermission(item.perm));

  // Render one nav item (page or shortcut). Locked items are greyed, non-clickable, and
  // explain the lack of access via tooltip.
  const renderNavItem = (item, groupKey) => {
    const unlocked = isUnlocked(item);
    const testid = groupKey && item.shortcut
      ? `nav-shortcut-${groupKey}-${item.tab}`
      : `nav-${item.label.toLowerCase().replace(/ /g, "-")}`;
    if (!unlocked) {
      return (
        <div
          key={testid}
          title="You don't have access to this."
          className="sidebar-link w-full text-left opacity-40 cursor-not-allowed"
          data-testid={`${testid}-locked`}
          aria-disabled="true"
        >
          <span className="shrink-0">{getIcon(item.icon)}</span>
          <span className="flex-1 min-w-0 leading-tight">{item.label}</span>
          <span className="ml-auto shrink-0 text-white/70">{getIcon("lock")}</span>
        </div>
      );
    }
    return (
      <button
        key={testid}
        onClick={() => { if (groupKey) setOpenGroup(groupKey); navigate(`${item.path}${item.query || ""}`); }}
        className={`sidebar-link w-full text-left ${isItemActive(item) ? "active" : ""}`}
        data-testid={testid}
      >
        <span className="shrink-0">{getIcon(item.icon)}</span>
        <span className="flex-1 min-w-0 leading-tight">{item.label}</span>
        {item.shortcut && (
          <span className="ml-auto shrink-0 text-white/50" title="Quick link to a Reports tab">{getIcon("arrow-up-right")}</span>
        )}
      </button>
    );
  };

  const getIcon = (name) => {
    const icons = {
      "grid-2x2": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/>
        </svg>
      ),
      "clipboard-check": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="m9 14 2 2 4-4"/>
        </svg>
      ),
      "file-text": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
        </svg>
      ),
      "users": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      "calendar": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      ),
      "bar-chart-2": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
      ),
      "contact": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 18a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2"/><rect width="18" height="18" x="3" y="4" rx="2"/><circle cx="12" cy="10" r="2"/><line x1="8" y1="2" x2="8" y2="4"/><line x1="16" y1="2" x2="16" y2="4"/>
        </svg>
      ),
      "message-square": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      ),
      "check-square": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
        </svg>
      ),
      "users-round": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 21a8 8 0 0 0-16 0"/><circle cx="10" cy="8" r="5"/><path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3"/>
        </svg>
      ),
      "user-plus": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
        </svg>
      ),
      "settings": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/>
        </svg>
      ),
      "shield": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      "archive": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="20" height="5" x="2" y="3" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="M10 12h4"/>
        </svg>
      ),
      "folder-lock": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v2"/><rect width="8" height="5" x="14" y="17" rx="1"/><path d="M20 17v-2a2 2 0 0 0-4 0v2"/>
        </svg>
      ),
      "dollar-sign": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      ),
      "log-out": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
      ),
      "trending-up": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
        </svg>
      ),
      "lock": (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      ),
      "truck": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>
        </svg>
      ),
      "brain": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/><path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/><path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/>
        </svg>
      ),
      "target": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
        </svg>
      ),
      "chevron-down": (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6"/>
        </svg>
      ),
      "calendar-check": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="m9 16 2 2 4-4"/>
        </svg>
      ),
      "life-buoy": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><path d="m4.93 4.93 4.24 4.24"/><path d="m14.83 9.17 4.24-4.24"/><path d="m14.83 14.83 4.24 4.24"/><path d="m9.17 14.83-4.24 4.24"/><circle cx="12" cy="12" r="4"/>
        </svg>
      ),
      "message-circle": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/>
        </svg>
      ),
      "briefcase": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/>
        </svg>
      ),
      "arrow-up-right": (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 7h10v10"/><path d="M7 17 17 7"/>
        </svg>
      ),
      "pill": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"/><path d="m8.5 8.5 7 7"/>
        </svg>
      ),
      "list": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
        </svg>
      ),
      "file-plus": (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
        </svg>
      ),
    };
    return icons[name] || null;
  };

  return (
    <div className="min-h-screen flex">
      {currentLocked && <Navigate to="/" replace />}
      <PrivacyGate />
      {/* Mobile Header */}
      {isMobile && (
        <header className="fixed top-0 left-0 right-0 h-14 z-40 flex items-center px-4 text-white" style={{ backgroundColor: 'var(--primary-color)' }}>
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="mobile-menu-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/>
            </svg>
          </button>
          <h1 className="ml-3 text-lg font-bold">SMARTIS</h1>
          <div className="ml-auto">
            <NotificationBell />
          </div>
        </header>
      )}

      {/* Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full w-64 text-white z-50 flex flex-col transition-transform duration-300 ease-in-out ${
          isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'
        }`} 
        style={{ backgroundColor: 'var(--primary-color)' }}
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo_full.png" alt="SMARTIS" className="h-10 w-auto rounded" />
            <div>
              <h1 className="text-lg font-bold tracking-tight">SMARTIS</h1>
              {user?.school_name && (
                <p
                  data-testid="sidebar-school-name"
                  className="text-xs text-white/70 truncate max-w-[9rem]"
                >
                  {user.school_name}
                </p>
              )}
            </div>
          </div>
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="close-sidebar-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>
        
        <div className="px-4 pt-3 pb-1">
          <GlobalSearch />
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {/* Dashboard — standalone, always visible, not in a group */}
          {renderNavItem(DASHBOARD_ITEM, null)}

          {NAV_GROUPS.map((group) => {
            const open = openGroup === group.key;
            return (
              <div key={group.key} className="pt-1">
                <button
                  onClick={() => setOpenGroup((prev) => (prev === group.key ? null : group.key))}
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 hover:bg-white/10 font-semibold text-white/90"
                  data-testid={`nav-group-${group.key}`}
                  aria-expanded={open}
                >
                  <span className="shrink-0">{getIcon(group.icon)}</span>
                  <span className="flex-1 min-w-0 text-left leading-tight">{group.name}</span>
                  <span className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}>{getIcon("chevron-down")}</span>
                </button>
                <div className={`grid transition-all duration-200 ease-in-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <div className="pl-3 pt-1 space-y-1">
                      {group.items.map((item) => renderNavItem(item, group.key))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-medium">
              {user?.name?.charAt(0) || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-white/60 truncate">{user?.positions?.join(", ")}</p>
            </div>
            {!settings.mobileMode && <NotificationBell />}
          </div>
          <button
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="sidebar-link w-full text-left text-white/70 hover:text-white"
            data-testid="logout-btn"
          >
            {getIcon("log-out")}
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 bg-muted/30 min-h-screen transition-all duration-300 overflow-x-hidden ${
        isMobile ? 'ml-0 pt-20 px-3 pb-4' : 'ml-64 p-8'
      }`}>
        {isTester() && (
          <div
            data-testid="reviewer-banner"
            className="mb-4 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
            <span>Reviewer mode — read-only sample data. Changes are disabled.</span>
          </div>
        )}
        {hasRole("HQ Viewer") && !isAdmin() && (
          <div
            data-testid="hq-viewer-banner"
            className="mb-4 flex items-center gap-2 rounded-lg border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
            <span>HQ Viewer — read-only access. You can view every section but cannot make changes.</span>
          </div>
        )}
        {children}
        <footer className="mt-10 pt-6 border-t border-slate-200 text-center text-xs text-muted-foreground" data-testid="site-footer">
          <p>
            &copy; {new Date().getFullYear()} SMARTIS Pro Software ·{" "}
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-[#00305E] hover:underline">
              Privacy Policy
            </Link>
          </p>
        </footer>
      </main>
    </div>
  );
};

// App Wrapper with Seed
const AppWrapper = () => {
  useEffect(() => {
    // Auto-seed data on first load
    seedData().catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <SettingsProvider>
        <AuthProvider>
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/self-assess" element={<SelfAssessmentPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/delete-data" element={<DataDeletionPage />} />
            <Route path="/discover" element={<MarketingPage />} />
            <Route
              path="/platform"
              element={
                <SuperAdminRoute>
                  <PlatformAdminPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="/portal"
              element={
                <PortalRoute>
                  <PortalPage />
                </PortalRoute>
              }
            />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DashboardPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/weekly-sheet"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <WeeklySheetPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brp"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <BrpPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AttendancePage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AssessmentPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/referrals"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ReferralTrackerPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/students"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <StudentsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <StudentDetailPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CalendarPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ReportsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/permissions"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <MainLayout>
                    <PermissionsPage />
                  </MainLayout>
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/archives"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <MainLayout>
                    <ArchivesPage />
                  </MainLayout>
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff-files"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <MainLayout>
                    <StaffFilesPage />
                  </MainLayout>
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <MainLayout>
                    <BillingDashboardPage />
                  </MainLayout>
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/advisory"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <AdvisoryPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SettingsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/contacts"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ContactsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/communications"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CommunicationsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quick-tasks"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <QuickTasksPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/proficiency-grid"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ProgressTrackingPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/progress-tracking"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ProgressTrackingPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/goals"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <GoalsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="bottom-right" />
          </ErrorBoundary>
        </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
};

export default AppWrapper;
